import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/pages/home/components/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

function getCategoryLabel(category: string): string {
  const map: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    variety: '综艺',
    anime: '动漫',
  };
  return map[category] || category;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;
  const relatedMovies = movie ? getRelatedMovies(movie.relatedIds) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-500 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-50 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该影视内容可能已下架或不存在</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-400 transition-all duration-300 whitespace-nowrap">
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
              <Link to="/" className="text-foreground-400 hover:text-primary-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
              <Link to={`/category/${movie.category}`} className="text-foreground-400 hover:text-primary-400 transition-colors">
                {getCategoryLabel(movie.category)}
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-500 text-sm"></i>
              <span className="text-foreground-200 font-medium">{movie.title}</span>
            </nav>

            {/* Detail Section */}
            <article className="mb-10 md:mb-14">
              <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
                {/* Poster */}
                <div className="shrink-0">
                  <div className="w-full sm:w-64 lg:w-72 aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 海报`}
                      title={`${movie.title} - ${movie.titleEn}`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-50 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm md:text-base text-foreground-400 mb-4">{movie.titleEn}</p>

                  {/* Meta */}
                  <div className="flex items-center gap-3 flex-wrap mb-5">
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-500/10 text-primary-400 text-sm font-semibold">
                      <i className="ri-star-fill text-sm"></i>
                      {movie.rating}
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-300 text-sm">
                      {movie.year}
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-300 text-sm">
                      {movie.region}
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-300 text-sm">
                      {movie.duration}
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-300 text-sm">
                      {getCategoryLabel(movie.category)}
                    </span>
                  </div>

                  {/* Genres */}
                  <div className="flex items-center gap-2 mb-5 flex-wrap">
                    {movie.genres.map((g) => (
                      <Link
                        key={g}
                        to={`/category/${movie.category}`}
                        className="px-3 py-1 rounded-full bg-secondary-500/10 text-secondary-300 text-xs font-medium hover:bg-secondary-500/20 transition-all duration-300"
                      >
                        {g}
                      </Link>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="mb-5">
                    <h2 className="text-sm font-semibold text-foreground-100 mb-2">剧情简介</h2>
                    <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                      {movie.description}
                    </p>
                  </div>

                  {/* Director & Actors */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap shrink-0">导演：</span>
                      <span className="text-sm text-foreground-200">{movie.director}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap shrink-0">主演：</span>
                      <span className="text-sm text-foreground-200">{movie.actors.join('、')}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="text-sm text-foreground-500 whitespace-nowrap shrink-0">更新：</span>
                      <span className="text-sm text-foreground-200">{movie.updateInfo}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap">
                      <i className="ri-play-fill text-lg"></i>
                      立即播放
                    </button>
                    <button className="flex items-center gap-2 px-5 py-3 rounded-full bg-background-100 text-foreground-200 font-medium text-sm hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap">
                      <i className="ri-heart-line text-base"></i>
                      收藏
                    </button>
                    <button className="flex items-center gap-2 px-5 py-3 rounded-full bg-background-100 text-foreground-200 font-medium text-sm hover:bg-background-200 transition-all duration-300 cursor-pointer whitespace-nowrap">
                      <i className="ri-share-forward-line text-base"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Related */}
            {relatedMovies.length > 0 && (
              <section className="pb-14" aria-label="相关推荐">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedMovies.map((m) => (
                    <MovieCard key={m.id} movie={m} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* Schema.org */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.chunyecinema.com/" },
            { "@type": "ListItem", "position": 2, "name": getCategoryLabel(movie.category), "item": `https://www.chunyecinema.com/category/${movie.category}` },
            { "@type": "ListItem", "position": 3, "name": movie.title, "item": `https://www.chunyecinema.com/article/${movie.id}` }
          ]
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Movie",
          "name": movie.title,
          "description": movie.description,
          "dateCreated": String(movie.year),
          "director": { "@type": "Person", "name": movie.director },
          "actor": movie.actors.map(a => ({ "@type": "Person", "name": a })),
          "image": movie.poster,
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": String(movie.rating),
            "bestRating": "10",
            "ratingCount": Math.floor(Math.random() * 100000) + 10000
          }
        }) }} />
      </main>

      <Footer />
    </div>
  );
}